import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import type { OrderSummaryAdapters } from '../../../module/order/orderAdapters';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { StripJsonLd } from '@vs-shop/schemas/transform/withoutJsonLd';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/orders';
const HTTP_METHOD = 'post';

export type MakeCreateOrderDeps<OShowType> = {
  readonly orderShow: Schema.Codec<OShowType, unknown>;
  readonly orderSummaryAdapters: OrderSummaryAdapters<StripJsonLd<OShowType>>;
};

export const makeCreateOrder = <OShowType>(deps: MakeCreateOrderDeps<OShowType>) => {
  const CreateOrderError = createBackendApiError({
    domain: 'Order',
    endpoint: 'CreateOrder',
    path: ENDPOINT,
    identifierField: 'orderToken',
  } as const);

  const postOrderFetch = Effect.fn(`http.${HTTP_METHOD} ${ENDPOINT}`, {
    kind: 'client',
    attributes: {
      'http.request.method': HTTP_METHOD,
    },
  })(function* () {
    const apiClient = yield* BackendApi;
    return yield* apiClient
      .path(ENDPOINT)
      .method(HTTP_METHOD)
      .accept('application/ld+json', 'application/json')
      .fetch({ body: {} });
  });

  const createOrder = () =>
    postOrderFetch().pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.orderShow)(response),
          404: (response) =>
            Effect.fail(new CreateOrderError.NotFoundError({ orderToken: 'new', response })),
          422: CreateOrderError.createViolationError('new'),
          orElse: (response) =>
            Effect.fail(new CreateOrderError.ApiRequestError({ orderToken: 'new', response })),
        })
      ),
      Effect.tap((order) =>
        logApiOperation(
          ApiModule.OrderCreate,
          'Debug',
          `Successfully created order -> tokenValue: ${deps.orderSummaryAdapters.tokenValue(order) ?? 'unknown'}`
        )
      ),
      Effect.withSpan('api.order.create', {
        kind: 'internal',
        attributes: {},
      })
    );

  return { createOrder, CreateOrderError };
};
